import _, {compact} from 'lodash';
import {connect} from 'react-redux';

import {UnreachableError} from '../../../../../libs/shared/core/src/helpers/types/customErrors';
import {globalNamespace} from '../../../../../libs/shared/core/src/models/helpers/namespaceHelpers';
import {
  createSelector,
  makeMapDispatchToProps,
  makeMapStateToProps,
} from '../../../../../srcCommon/helpers/reselectHelpers';
import {RefinementOptionModel} from '../../../../models/api/refinementOptionModel';
import {AppState} from '../../../../store/appState';
import {getCompany} from '../../../../store/data/company/companySelectors';
import {getInboxes} from '../../../../store/data/inboxes/inboxesSelectors';
import {getLenses} from '../../../../store/data/lenses/lensesSelectors';
import {getShouldUseOpenFilterForTeams} from '../../../../store/data/preferences/preferencesSelectors';
import {selectRefinementAction} from '../../../../store/data/refinements/refinementsActions';
import {
  makeGetSelectedAssignee,
  makeGetSelectedInbox,
  makeGetSelectedTag,
} from '../../../../store/data/refinements/refinementsSelectors';
import {getUserNamespace} from '../../../../store/data/teammates/teammatesSelectors';
import {getTeams, getUserTeams} from '../../../../store/data/teams/teamsSelectors';
import {CellsPanelPageState} from '../../../../store/panels/cells/cellsPanelState';
import {
  InboxesParametersTypesEnum,
  RefinedInboxParameters,
} from '../../../../store/panels/inboxes/parameters/inboxesParameters';
import {findSelectedRefinementFromProps} from '../../../../store/panels/inboxes/parameters/inboxesParametersHelpers';
import {CellsRefinementOptions} from './cellsRefinementOptions';

/*
 * Props.
 */

interface CellsRefinementOptionsContainerProps {
  parameters: RefinedInboxParameters;
  page: CellsPanelPageState;
}

/*
 * Redux.
 */

function getParameters(_state: AppState, props: CellsRefinementOptionsContainerProps) {
  return props.parameters;
}

function getPage(_state: AppState, props: CellsRefinementOptionsContainerProps) {
  return props.page;
}

function makeGetTagNamespaces() {
  return createSelector(
    [getParameters, getUserNamespace, getInboxes, getTeams, getLenses, getUserTeams],
    (parameters, userNamespace, inboxes, teams, lenses, userTeams): ReadonlyArray<string> => {
      switch (parameters.type) {
        case InboxesParametersTypesEnum.INBOX:
          return [globalNamespace, userNamespace, ...compact([inboxes[parameters.inboxId]?.namespace])];

        case InboxesParametersTypesEnum.TEAM:
          return [globalNamespace, userNamespace, ...compact([teams[parameters.teamId]?.namespace])];

        case InboxesParametersTypesEnum.TEAMMATE_LENS:
          return [
            globalNamespace,
            userNamespace,
            ..._(lenses[parameters.lensId]?.inboxIds ?? [])
              .map((inboxId) => inboxes[inboxId]?.namespace)
              .compact()
              .uniq()
              .value(),
          ];

        case InboxesParametersTypesEnum.TEAM_LENS:
          return [globalNamespace, userNamespace, ...compact([lenses[parameters.lensId]?.namespace])];

        case InboxesParametersTypesEnum.USER:
          return [globalNamespace, userNamespace, ...userTeams.map((team) => team.namespace)];

        case InboxesParametersTypesEnum.TEAMMATE_INBOX:
          return _([
            globalNamespace,
            userNamespace,
            ...userTeams.map((team) => team.namespace),
            ...compact([inboxes[parameters.inboxId]?.namespace]),
          ])
            .uniq()
            .value();
        default:
          throw new UnreachableError(parameters);
      }
    },
  );
}

function makeGetSelectedRefinement() {
  return createSelector([getParameters, getPage], (parameters, page): RefinementOptionModel | undefined =>
    findSelectedRefinementFromProps(parameters, page),
  );
}

function getRefinedResultsCount() {
  return createSelector([getPage], (page) =>
    page.refinedResultsCount === undefined
      ? undefined
      : page.refinedResultsCount + page.refinedResultsCountDelta,
  );
}

const mapStateToProps = makeMapStateToProps<CellsRefinementOptionsContainerProps>()(() => {
  const getSelectedRefinement = makeGetSelectedRefinement();
  return {
    company: getCompany,
    parameters: getParameters,
    shouldUseOpenFilterForTeams: getShouldUseOpenFilterForTeams,
    tagNamespaces: makeGetTagNamespaces(),
    resultsCount: getRefinedResultsCount(),
    selectedRefinement: getSelectedRefinement,
    selectedAssignee: makeGetSelectedAssignee(getSelectedRefinement),
    selectedInbox: makeGetSelectedInbox(getSelectedRefinement),
    selectedTag: makeGetSelectedTag(getSelectedRefinement),
  };
});

const mapDispatchToProps = makeMapDispatchToProps()(() => ({
  selectRefinement: selectRefinementAction,
}));

export const CellsRefinementOptionsContainer = connect(
  mapStateToProps,
  mapDispatchToProps,
  (stateProps, dispatchProps, ownProps: CellsRefinementOptionsContainerProps) => {
    const {parameters} = ownProps;
    const {shouldUseOpenFilterForTeams} = stateProps;
    return {
      ...stateProps,
      onRefinementOptionSelect: (selection: RefinementOptionModel) =>
        dispatchProps.selectRefinement(parameters, selection, shouldUseOpenFilterForTeams),
    };
  },
)(CellsRefinementOptions);
